import { trialClassNotifications } from '@common/enums'
import { CClient, flattenErrorsDictValues, showErrorAlert } from '@common/utils'
import { Dispatch, SetStateAction } from 'react'
import date from '@lib/utils/date.utils'
import { isDummyEmail } from '@lib/utils/identifier'
import { trackPageVisit } from '@common/utils'
import { defineMessage, t } from '@lingui/macro'

const DEFAULT_RANDOM_SLOT_RANGE = {
  start: 15,
  end: 21,
}

type GetRandomSlotOptionType = {
  slots: string[]
  selectedTimezone: string
  rangeStartHour?: number
  rangeEndHour?: number
}

const getRandomSlot = ({
  slots,
  selectedTimezone,
  rangeStartHour = DEFAULT_RANDOM_SLOT_RANGE.start,
  rangeEndHour = DEFAULT_RANDOM_SLOT_RANGE.end,
}: GetRandomSlotOptionType) => {
  if (!slots || slots?.length === 0) return null
  const slotsInRange = slots?.filter((slot) => {
    const slotHour = date(slot).tz(selectedTimezone).hour()
    return slotHour >= rangeStartHour && slotHour <= rangeEndHour - 1
  })

  if (slotsInRange?.length === 0) return null

  const randomIndex = Math.floor(Math.random() * (slotsInRange?.length - 1))
  return slotsInRange?.[randomIndex]
}

/**
 * When the server built `response`, from its `Date` header, as UTC
 * 'YYYY-MM-DD HH:mm'. Far older than the moment it is read means a stored copy
 * was handed back instead of a fresh one. Null when the header is missing.
 */
export const getResponseDateTime = (response: Response): string | null => {
  const serverDate = date(response.headers.get('Date') ?? '')
  return serverDate.isValid()
    ? serverDate.utc().format('YYYY-MM-DD HH:mm')
    : null
}

type GetAvailableTrialSlotsOptionType = {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  studentId: number
  selectedTimezone: string
  course: string
  teacherUuid?: number
  numDaysToShow?: number
  excludeApollo?: boolean
  setSelectedDate: Dispatch<SetStateAction<string>>
  setAvailableSlots: Dispatch<SetStateAction<any>>
  setError?: Dispatch<SetStateAction<string>>
  setSelectedTime?: Dispatch<SetStateAction<string>>
  setSlotsResponseDateTime?: Dispatch<SetStateAction<string | null>>
}

export const getAvailableTrialSlots = async ({
  setIsLoading,
  studentId,
  selectedTimezone,
  course,
  teacherUuid,
  setSelectedDate,
  setAvailableSlots,
  setError = null,
  setSelectedTime = undefined,
  setSlotsResponseDateTime = undefined,
  numDaysToShow,
  excludeApollo = false,
}: GetAvailableTrialSlotsOptionType) => {
  setIsLoading(true)

  let url = `/api/v1/calendars/trial/slots/?studentId=${studentId}&tz=${selectedTimezone}&course=${course}${
    numDaysToShow ? `&numDaysToShow=${numDaysToShow}` : ''
  }`
  if (teacherUuid) url += `&tid=${teacherUuid}`
  if (excludeApollo) url += '&exclude_apollo=yes'

  try {
    // Never reuse a stored copy: some students' mobile browsers showed a
    // weeks-old slot list, with past slots, instead of asking the server.
    const response = await CClient(url, { cache: 'no-store' })
    if (response.ok) {
      const data = await response.json()
      setSlotsResponseDateTime?.(getResponseDateTime(response))
      const slots: Record<string, string[]> = {}
      if (data.length === 0) {
        trackPageVisit(trialClassNotifications.NO_SLOT_AVAILABLE, course)
      }
      data.forEach((slot) => {
        const startDate = date(slot.start)
          .tz(selectedTimezone)
          .format('DD-MM-YYYY')

        // eslint-disable-next-line no-prototype-builtins
        if (!slots.hasOwnProperty(startDate)) {
          slots[startDate] = []
        }
        slots[startDate].push(date(slot.start).tz(selectedTimezone).format())
      })
      setSelectedDate(Object.keys(slots)[0])
      setSelectedTime?.(
        getRandomSlot({ slots: Object.values(slots)[0], selectedTimezone })
      )
      setAvailableSlots(slots)
      setIsLoading(false)
    } else {
      if (response.status === 403 || response.status === 404) {
        const errorData = await response.json()
        if (setError) setError(Object.values<string>(errorData)[0]) // To remove error prefix from the error message
        const error = flattenErrorsDictValues(errorData)
        throw new Error(error.join('\n'))
      }
      throw new Error()
    }
  } catch (error) {
    setIsLoading(false)
    const errorMessage = t({
      id: 'trialClass.booking.getAvailableSlotsErrorMessage',
      message:
        'There was an error getting available slots for a trial class. Please try again later or contact support to get help with booking the class.',
    })

    if (setError) {
      if (!error.message) {
        setError(errorMessage)
      }
    } else {
      showErrorAlert({ text: errorMessage })
    }
  }
}

/**
 * The address to show in the details form, or nothing.
 *
 * A stored address on `codingal-student.com` is the dummy written after the
 * one the parent typed came back undeliverable. Showing it would be absurd,
 * and prefilling what they typed instead invites them to submit the same bad
 * address again - the form checks the shape of an address, not whether it can
 * receive mail. The callout above the field names the one that failed; this
 * leaves them somewhere to type a new one.
 *
 * Any dummy, not just one built from the number the account holds now. This
 * used to rebuild the address from `phone` and compare, which stopped
 * matching as soon as the number changed - and then prefilled the dummy into
 * the field for the parent to submit.
 */
export const getInitialEmailValue = (userEmail: string) =>
  isDummyEmail(userEmail) ? '' : userEmail

export const getInitialGenderObj = (gender: string) => ({
  value: gender ? gender : 'select',
  label: gender
    ? gender
    : defineMessage({
        id: 'dropdown.gender.selectGender',
        message: 'Select gender',
      }),
})

export const trackTrialSlotViewed = (course: string | string[]) => {
  const courseStr = typeof course === 'string' ? course : course?.[0] || ''
  return trackPageVisit(trialClassNotifications.TRIAL_SLOT_VIEWED, courseStr)
}

// Mirrors SAME_DAY_BOOKING_CUTOFF in api/apps/calendarapp/slot_supply.py: past
// 6 PM in the student's timezone, D0 rolls from today to tomorrow.
const SAME_DAY_BOOKING_CUTOFF_HOUR = 18

// Return D0 and D1 as dayjs dates in `timezone`, judged the same way as the
// backend's `resolve_alert_days`: D0 is today until 6 PM local, then tomorrow;
// D1 always follows D0.
const resolveDayZeroDayOne = (timezone: string, now: string | Date) => {
  const nowLocal = date(now).tz(timezone)
  const dayZero =
    nowLocal.hour() >= SAME_DAY_BOOKING_CUTOFF_HOUR
      ? nowLocal.add(1, 'day')
      : nowLocal
  return { dayZero, dayOne: dayZero.add(1, 'day') }
}

/**
 * Count the slots on D0 and D1. `slots` is keyed by 'DD-MM-YYYY' in
 * `timezone`, as built by `getAvailableTrialSlots`.
 */
export const getDayZeroDayOneSlotCounts = (
  slots: Record<string, string[]>,
  timezone: string,
  now: string | Date = new Date()
) => {
  const { dayZero, dayOne } = resolveDayZeroDayOne(timezone, now)
  return {
    dayZero: slots[dayZero.format('DD-MM-YYYY')]?.length ?? 0,
    dayOne: slots[dayOne.format('DD-MM-YYYY')]?.length ?? 0,
  }
}

/**
 * The dates behind the D0/D1 counts, and when the student's first slot is, so
 * a 0 on D0 and D1 can be told apart from a wrong D0. All student-local; dates
 * as 'YYYY-MM-DD' and the first slot as 'YYYY-MM-DD HH:mm'.
 */
export const getSlotDayDates = (
  slots: Record<string, string[]>,
  timezone: string,
  now: string | Date = new Date()
) => {
  const { dayZero, dayOne } = resolveDayZeroDayOne(timezone, now)
  const firstSlot = Object.values(slots)
    .flat()
    .map((slot) => date(slot).tz(timezone))
    .reduce(
      (earliest, slotStart) =>
        earliest === null || slotStart.isBefore(earliest)
          ? slotStart
          : earliest,
      null
    )
  return {
    dayZeroDate: dayZero.format('YYYY-MM-DD'),
    dayOneDate: dayOne.format('YYYY-MM-DD'),
    firstSlotDateTime: firstSlot?.format('YYYY-MM-DD HH:mm') ?? null,
  }
}

// Mirrors PEAK_HOUR_START / PEAK_HOUR_END in api/apps/calendarapp/slot_supply.py:
// 4 PM to 8 PM in the student's timezone, both ends inclusive.
const PEAK_HOUR_START_MINUTES = 16 * 60
const PEAK_HOUR_END_MINUTES = 20 * 60

/**
 * Count every slot the student sees that starts inside the peak band, judged
 * the same way as the backend's `is_peak_hour`.
 */
export const getPeakHourSlotCount = (
  slots: Record<string, string[]>,
  timezone: string
) =>
  Object.values(slots)
    .flat()
    .filter((slot) => {
      const slotStart = date(slot).tz(timezone)
      const minutes = slotStart.hour() * 60 + slotStart.minute()
      return (
        minutes >= PEAK_HOUR_START_MINUTES &&
        // 8:00 PM is in, 8:00:30 PM would not be - same as comparing times.
        (minutes < PEAK_HOUR_END_MINUTES ||
          (minutes === PEAK_HOUR_END_MINUTES && slotStart.second() === 0))
      )
    }).length
